import React, { JSX, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  CheckBadgeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  TrashIcon,
  ShieldExclamationIcon,
  BoltIcon
} from '@heroicons/react/24/outline'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { MerchAdminSection } from 'components/admin/MerchAdminSection'
import { YouTubeSpeedPanel } from 'components/admin/YouTubeSpeedPanel'
import { CursorPager } from 'components/admin/CursorPager'
import { AdminListToolbar, ChipRow, PageSizePicker, SearchBox } from 'components/admin/AdminListToolbar'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { LoadingPage, LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { eventPath, ADMIN_STREAMS_PATH } from 'lib/paths'
import { guidanceFor } from 'lib/revocationCopy'
import {
  AdminSubscriptionRow,
  BotInfo,
  SubscriptionPage,
  TwitchAuthUrl,
  EventStatus,
  FleetCountsMonth,
  SetAccountSuspendedResult,
  UnsubscribeResult,
  ReconnectBotResult,
  WaitlistEntry,
  WaitlistPage,
  YouTubeDemoExemptResult,
  YouTubeInviteResult,
  YouTubeTelemetry,
  YouTubeTelemetryDay
} from 'types/api'
import {
  createPagerGate,
  createRequestRace,
  groupByBroadcaster,
  pageNumberOf,
  parseChoice,
  parsePageSize,
  popToken,
  pushToken,
  resetTokens,
  tokenForPage
} from 'lib/adminListState'
import getBotInfoQuery from 'graphql/getBotInfo.graphql'
import setAccountSuspendedMutation from 'graphql/setAccountSuspended.graphql'
import setYouTubeInviteMutation from 'graphql/setYouTubeInvite.graphql'
import setYouTubeDemoExemptMutation from 'graphql/setYouTubeDemoExempt.graphql'
import listYouTubeTelemetryQuery from 'graphql/listYouTubeTelemetry.graphql'
import listSubscriptionsPageQuery from 'graphql/listSubscriptionsPage.graphql'
import listYouTubeWaitlistQuery from 'graphql/listYouTubeWaitlist.graphql'
import requestTwitchAuthUrlMutation from 'graphql/requestTwitchAuthUrl.graphql'
import unsubscribeChannelMutation from 'graphql/unsubscribeChannel.graphql'
import reconnectBotMutation from 'graphql/reconnectBot.graphql'
import getFleetCountsQuery from 'graphql/getFleetCounts.graphql'

const YouTubeWordmark = (): JSX.Element => (
  <img src='/images/yt_logo_fullcolor_white_digital.png' alt='YouTube' className='h-4 w-auto' />
)

const STATUS_PILL: Record<string, string> = {
  enabled: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30',
  webhook_callback_verification_pending: 'bg-amber-500/15 text-amber-400 ring-amber-500/30',
  revoked: 'bg-red-500/15 text-red-300 ring-red-500/30'
}

const statusPillFor = (status: string): string =>
  STATUS_PILL[status] ?? 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'

const ErrorBanner = ({ message, onRetry }: { message: string, onRetry: () => void }): JSX.Element => (
  <div
    data-testid='bots-error'
    className='flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300'
  >
    <ExclamationTriangleIcon className='size-5 shrink-0' aria-hidden='true' />
    <div className='flex-1'>
      <p className='font-medium'>Couldn&apos;t load bot data</p>
      <p className='mt-1 text-xs text-red-300/80'>{message}</p>
    </div>
    <button
      onClick={onRetry}
      className='shrink-0 rounded-md border border-red-500/30 px-3 py-1.5 text-xs font-medium hover:bg-red-500/20'
    >
      Retry
    </button>
  </div>
)

const ConnectEmptyState = ({
  onConnect,
  connecting
}: {
  onConnect: () => void
  connecting: boolean
}): JSX.Element => (
  <div
    data-testid='bots-empty-state'
    className='flex flex-col items-center gap-4 text-center'
  >
    <h1 className='text-xl font-semibold text-dm-text-primary'>No bot connected</h1>
    <p className='max-w-md text-sm text-dm-text-secondary'>
      A bot is a Twitch account that listens to chat on your behalf. Sign in to Twitch as the
      account you want to use, then click below to authorize it.
    </p>
    <button
      onClick={onConnect}
      disabled={connecting}
      data-testid='connect-bot-button'
      className='inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
    >
      {connecting
        ? <><LoadingSpinner /> Opening Twitch…</>
        : <><img src={TWITCH_BRAND_ICON_SRC} alt='' className='h-4 w-auto' /> Connect Twitch bot</>}
    </button>
  </div>
)

const BotDetailCard = ({
  bot,
  connecting,
  revokedCount,
  reconnecting,
  reconnectError,
  onReauth,
  onReconnect,
  onDismissReconnectError
}: {
  bot: BotInfo
  connecting: boolean
  revokedCount: number
  reconnecting: boolean
  reconnectError: string | null
  onReauth: () => void
  onReconnect: () => void
  onDismissReconnectError: () => void
}): JSX.Element => {
  const guidance = bot.needsReauth ? guidanceFor(bot.lastRevocationReason) : null
  return (
    <section
      data-testid='bot-detail-card'
      className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-start gap-4'>
        <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand/15 text-base font-semibold text-brand'>
          {(bot.displayName ?? bot.login).slice(0, 2).toUpperCase()}
        </div>
        <div className='flex-1'>
          <div className='flex items-center gap-2'>
            <h2 className='text-lg font-semibold text-dm-text-primary'>
              {bot.displayName ?? bot.login}
            </h2>
            {bot.needsReauth
              ? (
                <span
                  data-testid='bot-needs-reauth-badge'
                  className='inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-300 ring-1 ring-inset ring-red-500/30'
                  title='Twitch revoked this bot: reauthorize to restore chat reading'
                >
                  <ShieldExclamationIcon className='size-3' aria-hidden='true' />
                  Reauthorize needed
                </span>
                )
              : (
                <span
                  className='inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'
                  title='Bot has authorized this app'
                >
                  <CheckBadgeIcon className='size-3' aria-hidden='true' />
                  Authorized
                </span>
                )}
          </div>
          <p className='mt-0.5 text-sm text-dm-text-secondary'>
            @{bot.login}
            <span className='mx-2 text-dm-text-accent'>·</span>
            <span className='text-dm-text-accent'>userId {bot.userId}</span>
          </p>
          {bot.connectedAt !== null && (
            <p className='mt-1 text-xs text-dm-text-accent'>
              Authorized on {formatLocalDateTime(bot.connectedAt)}
            </p>
          )}
          {bot.lastRevocationAt !== null && (
            <p className='mt-1 text-xs text-red-300/80'>
              Last revocation {formatLocalDateTime(bot.lastRevocationAt)}
              {bot.lastRevocationReason !== null && (
                <> · <span className='font-mono'>{bot.lastRevocationReason}</span></>
              )}
            </p>
          )}
        </div>
        <div className='flex shrink-0 flex-col gap-1.5'>
          <button
            onClick={onReauth}
            disabled={connecting}
            title='Re-authorize this bot'
            className='rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {connecting ? 'Opening…' : 'Re-authorize'}
          </button>
          {revokedCount > 0 && (
            <button
              data-testid='reconnect-channels-button'
              onClick={onReconnect}
              disabled={reconnecting}
              title='Recreate every revoked subscription for this bot'
              className='inline-flex items-center justify-center gap-1.5 rounded-md bg-red-500/20 px-3 py-1.5 text-xs font-semibold text-red-200 ring-1 ring-inset ring-red-500/40 hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {reconnecting
                ? <LoadingSpinner />
                : <BoltIcon className='size-3.5' aria-hidden='true' />}
              Reconnect {revokedCount} channel{revokedCount === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </div>
      {guidance !== null && (
        <div className='mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200'>
          <p className='font-medium text-red-100'>{guidance.title}</p>
          <p className='mt-1 text-red-200/85'>{guidance.message}</p>
        </div>
      )}

      {reconnectError !== null && (
        <div className='mt-3 flex items-start justify-between gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200'>
          <span>{reconnectError}</span>
          <button
            onClick={onDismissReconnectError}
            className='rounded p-0.5 text-red-200/70 hover:bg-red-500/10 hover:text-red-100'
          >
            ×
          </button>
        </div>
      )}

      {bot.scopes.length > 0 && (
        <div className='mt-5 border-t border-white/5 pt-4'>
          <h3 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Granted scopes
          </h3>
          <ul className='mt-2 flex flex-wrap gap-1.5'>
            {bot.scopes.map((s) => (
              <li
                key={s}
                className='rounded-md border border-white/10 bg-dm-bg-accent/40 px-2 py-0.5 font-mono text-[11px] text-dm-text-secondary'
              >
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

const STATUS_DOT: Record<EventStatus, string> = {
  ACTIVE: 'bg-emerald-500',
  INACTIVE: 'bg-amber-500',
  ENDED: 'bg-zinc-500'
}

const SUB_STATUS_DOT: Record<string, string> = {
  enabled: 'bg-emerald-500',
  webhook_callback_verification_pending: 'bg-amber-500',
  revoked: 'bg-red-500'
}

const subStatusDotFor = (status: string): string =>
  SUB_STATUS_DOT[status] ?? 'bg-zinc-500'

const isChatRow = (sub: AdminSubscriptionRow): boolean =>
  sub.kind !== 'subevent' && sub.type === 'channel.chat.message'

const shortTypeOf = (type: string): string => type.replace(/^channel\./, '')

const SUBS_VIEWS = ['attention', 'healthy', 'revoked', 'pending'] as const

const ChatSubscriptionLine = ({
  sub,
  busy,
  onForceUnsubscribe
}: {
  sub: AdminSubscriptionRow
  busy: boolean
  onForceUnsubscribe: () => void
}): JSX.Element => {
  const isRevoked = sub.status === 'revoked'
  const revocationGuidance = isRevoked ? guidanceFor(sub.revocationReason) : null
  const orphanHint = sub.canForceUnsubscribe && !isRevoked
  return (
    <div data-testid={`subscription-${sub.subscriptionId}`} className='flex flex-col gap-1.5'>
      <div className='flex flex-wrap items-center gap-2'>
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-inset ${statusPillFor(sub.status)}`}
        >
          {sub.status.replaceAll('_', ' ')}
        </span>
        <span className='truncate text-[11px] text-dm-text-accent'>
          <span className='font-mono'>{sub.type}</span>
          {sub.botUserId != null && <> · bot {sub.botUserId}</>}
        </span>
        {orphanHint && (
          <span className='italic text-[11px] text-amber-300/80'>
            orphaned: not bound to any stream
          </span>
        )}
        {sub.canForceUnsubscribe && (
          <button
            data-testid={`force-unsubscribe-${sub.subscriptionId}`}
            onClick={onForceUnsubscribe}
            disabled={busy}
            title={isRevoked ? 'Remove this revoked sub row' : 'Force unsubscribe this orphan'}
            className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            {isRevoked ? 'Remove' : 'Force unsubscribe'}
          </button>
        )}
      </div>
      {revocationGuidance != null && (
        <div className='rounded-md border border-red-500/20 bg-red-500/5 p-2 text-[11px] text-red-200'>
          <p className='font-medium text-red-100'>{revocationGuidance.title}</p>
          <p className='mt-0.5 text-red-200/80'>{revocationGuidance.message}</p>
          {sub.revokedAt != null && (
            <p className='mt-1 text-[10px] text-red-200/60'>
              Revoked {formatLocalDateTime(sub.revokedAt)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

const BroadcasterCard = ({
  group,
  unsubscribingId,
  onForceUnsubscribe
}: {
  group: { broadcasterUserId: string, rows: AdminSubscriptionRow[] }
  unsubscribingId: string | null
  onForceUnsubscribe: (sub: AdminSubscriptionRow) => void
}): JSX.Element => {
  const chatRows = group.rows.filter((r) => isChatRow(r))
  const subEventRows = group.rows.filter((r) => !isChatRow(r))
  const login = (chatRows[0] ?? group.rows[0]).channelLogin
  const anyRevoked = group.rows.some((r) => r.status === 'revoked')
  const usedBy = new Map<string, AdminSubscriptionRow['boundEvents'][number]>()
  for (const row of group.rows) {
    for (const ev of row.boundEvents) usedBy.set(ev.eventId, ev)
  }
  return (
    <li
      data-testid={`subscription-group-${group.broadcasterUserId}`}
      className={`flex flex-col gap-2 rounded-lg border px-3 py-2.5 ${
        anyRevoked ? 'border-red-500/30 bg-red-500/5' : 'border-white/5 bg-dm-bg-accent/30'
      }`}
    >
      <div className='flex min-w-0 items-center gap-3'>
        <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
        <div className='flex min-w-0 flex-col leading-tight'>
          <span className='truncate text-sm font-medium text-dm-text-primary'>{login}</span>
          <span className='truncate text-[11px] text-dm-text-accent'>
            id {group.broadcasterUserId}
          </span>
        </div>
      </div>

      <div className='flex flex-col gap-2 pl-7'>
        {chatRows.map((sub) => (
          <ChatSubscriptionLine
            key={sub.subscriptionId}
            sub={sub}
            busy={unsubscribingId === sub.subscriptionId}
            onForceUnsubscribe={() => onForceUnsubscribe(sub)}
          />
        ))}

        {subEventRows.length > 0 && (
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-dm-text-secondary'>
            <span
              title='Connect-time sub-event subscriptions (gift/sub tracking). Not chat listeners and never force-unsubscribed here; removed by the last account disconnect.'
              className='rounded-full bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300 ring-1 ring-inset ring-indigo-500/30'
            >
              sub-event
            </span>
            {subEventRows.map((sub) => (
              <span
                key={sub.subscriptionId}
                data-testid={`subscription-${sub.subscriptionId}`}
                title={`${sub.type}: ${sub.status.replace(/_/g, ' ')}`}
                className='inline-flex items-center gap-1.5'
              >
                <span className={`h-1.5 w-1.5 rounded-full ${subStatusDotFor(sub.status)}`} />
                <span className='font-mono'>{shortTypeOf(sub.type)}</span>
              </span>
            ))}
          </div>
        )}

        <div className='flex flex-wrap items-center gap-1.5 text-[11px]'>
          {usedBy.size > 0 && (
            <>
              <span className='text-dm-text-accent'>Used by:</span>
              {[...usedBy.values()].map((ev) => (
                <Link
                  key={ev.eventId}
                  to={eventPath(ev.eventId)}
                  className='inline-flex items-center gap-1 rounded-md border border-white/10 bg-dm-bg-secondary px-2 py-0.5 text-dm-text-secondary hover:border-brand/40 hover:text-brand'
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[ev.status]}`} />
                  <span className='max-w-[12rem] truncate'>{ev.name}</span>
                </Link>
              ))}
            </>
          )}
          {usedBy.size === 0 && chatRows.length === 0 && (
            <span className='text-dm-text-accent'>
              Sub tracking. Kept while any account stays connected to this Twitch channel.
            </span>
          )}
        </div>
      </div>
    </li>
  )
}

const SubscriptionsCard = ({
  page,
  loading,
  error,
  onRetry,
  view,
  onView,
  pageSize,
  onPageSize,
  urlNotice,
  activeSearch,
  onSearch,
  pageNumber,
  onPrev,
  onNext,
  unsubscribingId,
  onForceUnsubscribe,
  forceError
}: {
  page: SubscriptionPage | null
  loading: boolean
  error: string | null
  onRetry: () => void
  view: string
  onView: (view: string) => void
  pageSize: number
  onPageSize: (size: number) => void
  urlNotice: string | null
  activeSearch: string | null
  onSearch: (term: string | null) => void
  pageNumber: number
  onPrev: () => void
  onNext: () => void
  unsubscribingId: string | null
  onForceUnsubscribe: (sub: AdminSubscriptionRow) => void
  forceError: string | null
}): JSX.Element => {
  const counts = page?.counts ?? null
  const chips = [
    { key: 'attention', label: 'Needs attention', count: counts == null ? null : counts.revoked + counts.pending, active: activeSearch == null && view === 'attention' },
    { key: 'healthy', label: 'Healthy', count: null, active: activeSearch == null && view === 'healthy' },
    { key: 'revoked', label: 'Revoked', count: counts == null ? null : counts.revoked, active: activeSearch == null && view === 'revoked' },
    { key: 'pending', label: 'Pending', count: counts == null ? null : counts.pending, active: activeSearch == null && view === 'pending' }
  ]
  const groups = groupByBroadcaster(page?.items ?? [])
  const hasNext = activeSearch == null && page?.nextToken != null
  return (
    <section
      data-testid='subscriptions-card'
      className='mt-5 rounded-xl border border-white/5 bg-dm-bg-secondary p-6'
    >
      <div className='mb-2'>
        <h3 className='text-base font-semibold text-dm-text-primary'>Subscribed channels (Twitch EventSub)</h3>
        <p className='mt-0.5 text-xs text-dm-text-secondary'>
          Channels are subscribed automatically when added to a stream and cleaned up when the last
          stream using them is ended or removes the binding. Only a chat subscription that no stream
          uses can be force-unsubscribed; sub-tracking rows are removed automatically when the last
          account connected to that Twitch channel disconnects it. A streamer&apos;s rows fold into
          one card per page; search shows the streamer&apos;s full group.
        </p>
      </div>

      <AdminListToolbar>
        <ChipRow label='Subscription views' chips={chips} onChip={onView} testid='subs-chip' />
        <div className='flex flex-wrap items-center gap-3'>
          <SearchBox
            placeholder='Exact Twitch login or numeric broadcaster id'
            label='Search subscriptions by exact Twitch login or numeric broadcaster id'
            activeTerm={activeSearch}
            onSubmit={onSearch}
            testid='subs-search'
          />
          <PageSizePicker value={pageSize} onChange={onPageSize} testid='subs-size' />
        </div>
      </AdminListToolbar>

      {urlNotice != null && (
        <p className='mt-2 text-xs text-amber-300' data-testid='subs-url-notice'>{urlNotice}</p>
      )}

      {forceError != null && (
        <div className='mt-3 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
          <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
          <span>{forceError}</span>
        </div>
      )}

      {error != null && (
        <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
          <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
          <span className='flex-1'>{error}</span>
          <button
            onClick={onRetry}
            className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
          >
            Retry
          </button>
        </div>
      )}

      {error == null && (loading && page == null
        ? (
          <div className='flex items-center justify-center py-8'>
            <LoadingSpinner />
          </div>
          )
        : groups.length === 0
          ? (
            <div className='mt-3 flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-sm text-dm-text-secondary' data-testid='subs-empty'>
              <CalendarDaysIcon className='size-6 text-dm-text-accent' aria-hidden='true' />
              {activeSearch != null
                ? <p>No subscription matches. Search is an exact Twitch login or a numeric broadcaster id.</p>
                : <p>No subscriptions in this view.</p>}
            </div>
            )
          : (
            <ul className='mt-3 space-y-2' data-testid='subs-list'>
              {groups.map((group) => (
                <BroadcasterCard
                  key={group.broadcasterUserId}
                  group={group}
                  unsubscribingId={unsubscribingId}
                  onForceUnsubscribe={onForceUnsubscribe}
                />
              ))}
            </ul>
            ))}

      <CursorPager
        page={pageNumber}
        hasNext={hasNext}
        loading={loading}
        onPrev={onPrev}
        onNext={onNext}
        testid='subs-pager'
      />
    </section>
  )
}

const suspendResultCopy = (email: string, res: SetAccountSuspendedResult): string => {
  if (res.suspended) {
    return `${email} suspended.${res.previouslySuspended ? ' (was already suspended)' : ''} Pasted overlay URLs die at their next load. Finish the runbook: remove the streamer role in Auth0 and rotate event overlay tokens for any live events.`
  }
  return `${email} unsuspended.${res.previouslySuspended ? '' : ' (was not suspended)'}`
}

const SuspendAccountCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState<'suspend' | 'unsuspend' | null>(null)
  const [result, setResult] = useState<{ email: string, res: SetAccountSuspendedResult } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (suspended: boolean): Promise<void> => {
    const trimmed = email.trim()
    if (trimmed.length === 0 || busy !== null) return
    setBusy(suspended ? 'suspend' : 'unsuspend')
    setError(null)
    setResult(null)
    try {
      const { result: res } = await graphqlRequest<{ result: SetAccountSuspendedResult }>(
        setAccountSuspendedMutation, { email: trimmed, suspended }, auth0Token
      )
      setResult({ email: trimmed, res })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set account suspension')
    } finally {
      setBusy(null)
    }
  }

  const disabled = busy !== null || email.trim().length === 0

  return (
    <section
      data-testid='suspend-account-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center gap-2'>
        <ShieldExclamationIcon className='size-5 text-red-300' aria-hidden='true' />
        <h3 className='text-base font-semibold text-dm-text-primary'>Account Suspension</h3>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Suspend or restore an account by user email. Suspending blocks
        serving and darkens pasted overlay URLs at their next load.
        Runbook reminder: remove the streamer role in Auth0 and rotate
        event overlay tokens for any live events.
      </p>
      <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <input
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder='user@example.com'
          disabled={busy !== null}
          data-testid='suspend-account-email-input'
          className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
        />
        <div className='flex shrink-0 items-center gap-2'>
          <button
            type='button'
            onClick={() => { void submit(true) }}
            disabled={disabled}
            data-testid='suspend-account-suspend-button'
            className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-2 text-sm font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'suspend' && <LoadingSpinner />}
            Suspend
          </button>
          <button
            type='button'
            onClick={() => { void submit(false) }}
            disabled={disabled}
            data-testid='suspend-account-unsuspend-button'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'unsuspend' && <LoadingSpinner />}
            Unsuspend
          </button>
        </div>
      </div>
      {result !== null && (
        <p
          data-testid='suspend-account-result'
          className={result.res.suspended ? 'mt-3 text-xs text-amber-300' : 'mt-3 text-xs text-emerald-300'}
        >
          {suspendResultCopy(result.email, result.res)}
        </p>
      )}
      {error !== null && (
        <div className='mt-3'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
    </section>
  )
}

const inviteResultCopy = (email: string, res: YouTubeInviteResult): { text: string, partial: boolean } => {
  if (res.youtubeChatInvited) {
    return {
      text: `${email} beta access granted.${res.previousValue ? ' (was already granted)' : ''}`,
      partial: false
    }
  }
  const r = res.revocation
  if (r == null) {
    return { text: `${email} beta access revoked.`, partial: false }
  }
  const failedParts: string[] = []
  if (r.hadConnection && !r.disconnected) failedParts.push('connection removal')
  if (!r.purgeComplete) failedParts.push('stored-data deletion')
  if (!r.consentCleared) failedParts.push('consent clearing')
  if (failedParts.length > 0) {
    return {
      text: `${email} beta access revoked, but some cleanup did not finish: ${failedParts.join(', ')}. Revoke again to retry — every step is safe to repeat.`,
      partial: true
    }
  }
  if (r.hadConnection) {
    return {
      text: `${email} beta access revoked. YouTube connection removed and Google access revoked; stored YouTube data deleted; consent cleared.`,
      partial: false
    }
  }
  return {
    text: `${email} beta access revoked. No YouTube connection was present; stored YouTube data deleted; consent cleared.`,
    partial: false
  }
}

const YouTubeInviteCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState<'grant' | 'revoke' | null>(null)
  const [result, setResult] = useState<{ email: string, res: YouTubeInviteResult } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (invited: boolean): Promise<void> => {
    const trimmed = email.trim()
    if (trimmed.length === 0 || busy !== null) return
    setBusy(invited ? 'grant' : 'revoke')
    setError(null)
    setResult(null)
    try {
      const { result: res } = await graphqlRequest<{ result: YouTubeInviteResult }>(
        setYouTubeInviteMutation, { email: trimmed, invited }, auth0Token
      )
      setResult({ email: trimmed, res })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set YouTube invite')
    } finally {
      setBusy(null)
    }
  }

  const disabled = busy !== null || email.trim().length === 0
  const copy = result !== null ? inviteResultCopy(result.email, result.res) : null

  return (
    <section
      data-testid='youtube-invite-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center gap-2'>
        <h3 className='text-base font-semibold text-dm-text-primary'>Beta Access</h3>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Grant or revoke an account&apos;s YouTube live-chat preview invite
        by user email. Revoking also disconnects the YouTube connection,
        deletes stored YouTube data, and clears consent: a re-invited
        user starts fresh.
      </p>
      <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <input
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder='user@example.com'
          disabled={busy !== null}
          data-testid='youtube-invite-email-input'
          className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
        />
        <div className='flex shrink-0 items-center gap-2'>
          <button
            type='button'
            onClick={() => { void submit(true) }}
            disabled={disabled}
            data-testid='youtube-invite-grant-button'
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'grant' && <LoadingSpinner />}
            Grant invite
          </button>
          <button
            type='button'
            onClick={() => { void submit(false) }}
            disabled={disabled}
            data-testid='youtube-invite-revoke-button'
            className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-2 text-sm font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'revoke' && <LoadingSpinner />}
            Revoke
          </button>
        </div>
      </div>
      {copy !== null && (
        <p
          data-testid='youtube-invite-result'
          className={copy.partial ? 'mt-3 text-xs text-amber-300' : 'mt-3 text-xs text-emerald-300'}
        >
          {copy.text}
        </p>
      )}
      {error !== null && (
        <div className='mt-3'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
    </section>
  )
}

const demoResultCopy = (email: string, res: YouTubeDemoExemptResult): string => {
  if (res.youtubeDemoExempt) return `${email} demo exemption granted.`
  return `${email} demo exemption cleared.`
}

const YouTubeDemoExemptCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState<'grant' | 'clear' | null>(null)
  const [result, setResult] = useState<{ email: string, res: YouTubeDemoExemptResult } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (exempt: boolean): Promise<void> => {
    const trimmed = email.trim()
    if (trimmed.length === 0 || busy !== null) return
    setBusy(exempt ? 'grant' : 'clear')
    setError(null)
    setResult(null)
    try {
      const { result: res } = await graphqlRequest<{ result: YouTubeDemoExemptResult }>(
        setYouTubeDemoExemptMutation, { email: trimmed, exempt }, auth0Token
      )
      setResult({ email: trimmed, res })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set YouTube demo exemption')
    } finally {
      setBusy(null)
    }
  }

  const disabled = busy !== null || email.trim().length === 0

  return (
    <section
      data-testid='youtube-demo-exempt-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center gap-2'>
        <h3 className='text-base font-semibold text-dm-text-primary'>Demo Exemption</h3>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Exempt the demo account&apos;s sessions from the global governor
        ceiling (the demo quota reservation funds them) and mark its
        consumption as demo in telemetry by user email; we resolve the
        account for you. The kill switch still stops exempt sessions.
      </p>
      <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <input
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder='user@example.com'
          disabled={busy !== null}
          data-testid='youtube-demo-email-input'
          className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
        />
        <div className='flex shrink-0 items-center gap-2'>
          <button
            type='button'
            onClick={() => { void submit(true) }}
            disabled={disabled}
            data-testid='youtube-demo-grant-button'
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'grant' && <LoadingSpinner />}
            Grant exemption
          </button>
          <button
            type='button'
            onClick={() => { void submit(false) }}
            disabled={disabled}
            data-testid='youtube-demo-clear-button'
            className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-2 text-sm font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'clear' && <LoadingSpinner />}
            Clear
          </button>
        </div>
      </div>
      {result !== null && (
        <p data-testid='youtube-demo-result' className='mt-3 text-xs text-emerald-300'>
          {demoResultCopy(result.email, result.res)}
        </p>
      )}
      {error !== null && (
        <div className='mt-3'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
    </section>
  )
}

const WaitlistRow = ({ row }: { row: WaitlistEntry }): JSX.Element => (
  <li className='flex flex-wrap items-center gap-2 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
    <span className={`min-w-0 flex-1 truncate text-xs ${row.email === null ? 'italic text-dm-text-accent' : 'text-dm-text-primary'}`}>
      {row.email ?? 'unknown user'}
    </span>
    {row.youtubeDemoExempt && (
      <span className='shrink-0 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand ring-1 ring-inset ring-brand/30'>
        demo
      </span>
    )}
    <span className='shrink-0 text-[11px] text-dm-text-accent'>
      {formatLocalDateTime(row.youtubeWaitlistAt)}
    </span>
  </li>
)

const WaitlistCardBody = ({
  rows,
  loading,
  error,
  onRetry
}: {
  rows: WaitlistEntry[] | null
  loading: boolean
  error: string | null
  onRetry: () => void
}): JSX.Element => {
  if (error !== null) {
    return (
      <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
        <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
        <span className='flex-1'>{error}</span>
        <button
          onClick={onRetry}
          className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
        >
          Retry
        </button>
      </div>
    )
  }
  if (loading || rows === null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary'>
        Nobody on the waitlist yet.
      </p>
    )
  }
  return (
    <ul className='mt-3 space-y-1.5'>
      {rows.map((row) => <WaitlistRow key={row.accountId} row={row} />)}
    </ul>
  )
}

const YouTubeWaitlistCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [page, setPage] = useState<WaitlistPage | null>(null)
  const [tokens, setTokens] = useState<string[]>(resetTokens())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const raceRef = useRef(createRequestRace())
  const gateRef = useRef(createPagerGate())

  const load = useCallback(async (stack: string[]): Promise<void> => {
    const requestId = raceRef.current.begin()
    gateRef.current.hold()
    setLoading(true)
    setError(null)
    try {
      const { page: fetched } = await graphqlRequest<{ page: WaitlistPage }>(
        listYouTubeWaitlistQuery, { limit: 50, nextToken: tokenForPage(stack) }, auth0Token
      )
      if (!raceRef.current.isCurrent(requestId)) return
      setPage(fetched)
      gateRef.current.settle(fetched.nextToken)
    } catch (err) {
      if (!raceRef.current.isCurrent(requestId)) return
      gateRef.current.fail()
      setError(err instanceof Error ? err.message : 'Failed to load the waitlist')
    } finally {
      if (raceRef.current.isCurrent(requestId)) setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void load(tokens)
  }, [load, tokens])

  return (
    <section
      data-testid='youtube-waitlist-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <YouTubeWordmark />
          <h3 className='text-base font-semibold text-dm-text-primary'>Waitlist</h3>
          {page != null && (
            <span
              data-testid='youtube-waitlist-count'
              className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand/15 px-2 text-xs font-semibold text-brand'
            >
              {page.totalWaiting}
            </span>
          )}
        </div>
        <button
          onClick={() => { void load(tokens) }}
          disabled={loading}
          title='Refresh the waitlist'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
          Refresh
        </button>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Accounts that asked for YouTube preview access, earliest first.
        Granting an invite clears the stamp, this
        list covers accounts still waiting.
      </p>
      <WaitlistCardBody rows={page?.items ?? null} loading={loading} error={error} onRetry={() => { void load(tokens) }} />
      <CursorPager
        page={pageNumberOf(tokens)}
        hasNext={page?.nextToken != null}
        loading={loading}
        onPrev={() => {
          if (!gateRef.current.takePrev()) return
          setLoading(true)
          setTokens((prev) => popToken(prev))
        }}
        onNext={() => {
          const token = gateRef.current.takeNext()
          if (token == null) return
          setLoading(true)
          setTokens((prev) => pushToken(prev, token))
        }}
        testid='waitlist-pager'
      />
    </section>
  )
}

const TelemetryCardBody = ({
  days,
  loading,
  error,
  onRetry
}: {
  days: YouTubeTelemetryDay[] | null
  loading: boolean
  error: string | null
  onRetry: () => void
}): JSX.Element => {
  if (error !== null) {
    return (
      <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
        <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
        <span className='flex-1'>{error}</span>
        <button
          onClick={onRetry}
          className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
        >
          Retry
        </button>
      </div>
    )
  }
  if (loading || days === null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (days.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary'>
        No telemetry recorded yet.
      </p>
    )
  }
  return (
    <div className='mt-3 overflow-x-auto'>
      <table className='w-full min-w-[720px] text-left text-[11px]'>
        <thead>
          <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            <th className='py-1.5 pr-3'>Date</th>
            <th className='py-1.5 pr-3'>Units</th>
            <th className='py-1.5 pr-3'>Demo units</th>
            <th className='py-1.5 pr-3'>Calls</th>
            <th className='py-1.5 pr-3'>Demo calls</th>
            <th className='py-1.5 pr-3'>Sessions</th>
            <th className='py-1.5 pr-3' title='Quota-rationing refusals only: ceiling + kill switch'>Refused (quota)</th>
            <th className='py-1.5 pr-3'>Refused (not live)</th>
            <th className='py-1.5 pr-3'>Refused (running)</th>
            <th className='py-1.5'>Peak units/min</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date} className='border-b border-white/5 text-dm-text-secondary'>
              <td className='py-1.5 pr-3 font-mono text-dm-text-primary'>{d.date}</td>
              <td className='py-1.5 pr-3'>{d.units}</td>
              <td className='py-1.5 pr-3'>{d.demoUnits}</td>
              <td className='py-1.5 pr-3'>{d.calls}</td>
              <td className='py-1.5 pr-3'>{d.demoCalls}</td>
              <td className='py-1.5 pr-3'>{d.sessionsStarted}</td>
              <td className='py-1.5 pr-3'>{d.refusedSessionStarts}</td>
              <td className='py-1.5 pr-3'>{d.refusedNotLive}</td>
              <td className='py-1.5 pr-3'>{d.refusedAlreadyRunning}</td>
              <td className='py-1.5'>{d.peakUnitsPerMinute}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const YouTubeTelemetryCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [days, setDays] = useState<YouTubeTelemetryDay[] | null>(null)
  const [quota, setQuota] = useState<{ budget: number, remainingToday: number } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const { telemetry } = await graphqlRequest<{ telemetry: YouTubeTelemetry }>(
        listYouTubeTelemetryQuery, {}, auth0Token
      )
      setDays([...telemetry.days].sort((a, b) => b.date.localeCompare(a.date)))
      setQuota({ budget: telemetry.budget, remainingToday: telemetry.remainingToday })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load telemetry')
    } finally {
      setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section
      data-testid='youtube-telemetry-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <h3 className='text-base font-semibold text-dm-text-primary'>Quota Telemetry</h3>
        </div>
        <button
          onClick={() => { void load() }}
          disabled={loading}
          title='Refresh telemetry'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
          Refresh
        </button>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        Global YouTube Data API consumption per day, our own counters
        only. Demo columns are the demo account&apos;s marked share of the
        totals; organic burn is totals minus demo.
      </p>
      {quota !== null && (
        <p data-testid='youtube-quota-remaining' className='mt-3 text-sm text-dm-text-primary'>
          Budget <span className='font-semibold'>{quota.budget.toLocaleString()}</span>
          <span className='mx-1.5 text-dm-text-accent'>·</span>
          Remaining today (Pacific day) <span className='font-semibold'>{quota.remainingToday.toLocaleString()}</span>
        </p>
      )}
      <TelemetryCardBody days={days} loading={loading} error={error} onRetry={() => { void load() }} />
    </section>
  )
}

const ADMIN_TOTALS_ERROR = "Couldn't load the totals."

const AdminTotalsSection = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [months, setMonths] = useState<FleetCountsMonth[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    const load = async (): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        const { months: rows } = await graphqlRequest<{ months: FleetCountsMonth[] | null }>(
          getFleetCountsQuery, {}, auth0Token
        )
        if (!cancelled) setMonths(rows ?? [])
      } catch {
        if (!cancelled) setError(ADMIN_TOTALS_ERROR)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [auth0Token, attempt])

  const latest = (months ?? [])[0] ?? null
  const asOf = latest != null && !Number.isNaN(Date.parse(latest.rolledUpAt))
    ? formatLocalDateTime(latest.rolledUpAt)
    : null

  return (
    <section data-testid='admin-totals-section' className='mb-8'>
      <div className='grid grid-cols-1 gap-5'>
        <div className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'>
          <div className='flex items-center justify-between gap-3'>
            <h2 className='text-lg font-semibold text-dm-text-primary'>Streams and users</h2>
            <Link
              to={ADMIN_STREAMS_PATH}
              data-testid='all-streams-link'
              className='text-xs font-medium text-dm-text-secondary underline-offset-2 hover:text-brand hover:underline'
            >
              All streams
            </Link>
          </div>

          {error !== null
            ? (
              <div className='mt-4 flex items-center gap-3 text-sm text-red-300' data-testid='admin-totals-error'>
                <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
                <span>{error}</span>
                <button
                  type='button'
                  onClick={() => setAttempt((n) => n + 1)}
                  disabled={loading}
                  data-testid='admin-totals-retry'
                  className='rounded-md border border-red-500/30 px-3 py-1 text-xs font-medium hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  Try again
                </button>
              </div>
              )
            : loading && months == null
              ? (
                <div className='mt-4 flex items-center py-2' data-testid='admin-totals-loading'>
                  <LoadingSpinner />
                </div>
                )
              : latest == null
                ? (
                  <p className='mt-4 text-sm text-dm-text-secondary' data-testid='admin-totals-empty'>
                    The nightly count has not run yet.
                  </p>
                  )
                : (
                  <>
                    <dl className='mt-4 flex flex-wrap gap-x-12 gap-y-3'>
                      <div title='Every stream, ended ones included.'>
                        <dt className='text-xs text-dm-text-secondary'>Streams</dt>
                        <dd data-testid='admin-totals-streams' className='mt-0.5 text-2xl font-semibold text-dm-text-primary'>
                          {latest.streamCount == null
                            ? <span className='text-sm font-medium text-dm-text-accent'>Not counted yet</span>
                            : latest.streamCount.toLocaleString()}
                        </dd>
                      </div>
                      <div title='Every account, suspended ones included. Deleted accounts are not counted.'>
                        <dt className='text-xs text-dm-text-secondary'>Users</dt>
                        <dd data-testid='admin-totals-users' className='mt-0.5 text-2xl font-semibold text-dm-text-primary'>
                          {latest.accountCount.toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                    <p data-testid='admin-totals-asof' className='mt-3 text-[11px] text-dm-text-accent'>
                      {asOf != null ? `as of last night (${asOf})` : 'as of last night'}
                    </p>
                  </>
                  )}
        </div>
        <YouTubeSpeedPanel auth0Token={auth0Token} />
      </div>
    </section>
  )
}

export const BotsPage = (): JSX.Element | null => {
  const { auth0Token, streamToolsUser } = useAuth()
  const navigate = useNavigate()
  const { superuser } = useEntitlements()
  useEffect(() => {
    if (streamToolsUser !== null && !superuser) {
      void navigate('/', { replace: true })
    }
  }, [streamToolsUser, superuser, navigate])
  const [bot, setBot] = useState<BotInfo | null>(null)
  const [botLoading, setBotLoading] = useState(true)
  const [botLoaded, setBotLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [unsubscribingId, setUnsubscribingId] = useState<string | null>(null)
  const [forceError, setForceError] = useState<string | null>(null)
  const [reconnecting, setReconnecting] = useState(false)
  const [reconnectError, setReconnectError] = useState<string | null>(null)
  const popupWatcherRef = useRef<ReturnType<typeof globalThis.setInterval> | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()
  const viewParsed = parseChoice(searchParams.get('view'), SUBS_VIEWS, 'attention', 'View')
  const sizeParsed = parsePageSize(searchParams.get('size'), 50)
  const subsView = viewParsed.value
  const subsPageSize = sizeParsed.value
  const subsUrlNotice = viewParsed.notice ?? sizeParsed.notice
  const [subsPage, setSubsPage] = useState<SubscriptionPage | null>(null)
  const [subsLoading, setSubsLoading] = useState(true)
  const [subsError, setSubsError] = useState<string | null>(null)
  const [subsTokens, setSubsTokens] = useState<string[]>(resetTokens())
  const [subsSearch, setSubsSearch] = useState<string | null>(null)
  const subsRaceRef = useRef(createRequestRace())
  const subsGateRef = useRef(createPagerGate())
  const subsListKey = `${subsView}|${subsPageSize}`
  const [subsTokensKey, setSubsTokensKey] = useState(subsListKey)
  if (subsTokensKey !== subsListKey) {
    setSubsTokensKey(subsListKey)
    setSubsTokens(resetTokens())
  }
  useLayoutEffect(() => {
    subsGateRef.current.drop()
  }, [subsListKey])

  const fetchBot = useCallback(async () => {
    if (auth0Token === undefined) return
    setBotLoading(true)
    setError(null)
    try {
      const botResp = await graphqlRequest<{ bot: BotInfo | null }>(getBotInfoQuery, {}, auth0Token)
      setBot(botResp.bot)
      setBotLoaded(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setBotLoading(false)
    }
  }, [auth0Token])

  const loadSubscriptions = useCallback(async (stack: string[], search: string | null): Promise<void> => {
    if (auth0Token == null) return
    const requestId = subsRaceRef.current.begin()
    subsGateRef.current.hold()
    setSubsLoading(true)
    setSubsError(null)
    try {
      const variables = search != null
        ? { limit: subsPageSize, search }
        : { limit: subsPageSize, nextToken: tokenForPage(stack), view: subsView }
      const { page: fetched } = await graphqlRequest<{ page: SubscriptionPage }>(
        listSubscriptionsPageQuery, variables, auth0Token
      )
      if (!subsRaceRef.current.isCurrent(requestId)) return
      setSubsPage(fetched)
      subsGateRef.current.settle(search != null ? null : fetched.nextToken)
    } catch (err) {
      if (!subsRaceRef.current.isCurrent(requestId)) return
      subsGateRef.current.fail()
      setSubsError(err instanceof Error ? err.message : 'Failed to load subscriptions')
    } finally {
      if (subsRaceRef.current.isCurrent(requestId)) setSubsLoading(false)
    }
  }, [auth0Token, subsPageSize, subsView])

  useEffect(() => {
    void fetchBot()
    return () => {
      if (popupWatcherRef.current !== null) {
        globalThis.clearInterval(popupWatcherRef.current)
      }
    }
  }, [fetchBot])

  const [subsReloadTick, setSubsReloadTick] = useState(0)
  const reloadSubscriptions = useCallback((): void => {
    subsGateRef.current.hold()
    setSubsLoading(true)
    setSubsReloadTick((tick) => tick + 1)
  }, [])

  useEffect(() => {
    void loadSubscriptions(subsTokens, subsSearch)
  }, [loadSubscriptions, subsTokens, subsSearch, subsReloadTick])

  const setSubsParam = (key: string, value: string): void => {
    const next = new URLSearchParams(globalThis.location.search)
    next.set(key, value)
    setSearchParams(next)
  }

  const onSubsView = (view: string): void => {
    subsGateRef.current.drop()
    setSubsSearch(null)
    setSubsTokens(resetTokens())
    setSubsLoading(true)
    setSubsParam('view', view)
  }

  const onSubsPageSize = (size: number): void => {
    subsGateRef.current.drop()
    setSubsTokens(resetTokens())
    setSubsLoading(true)
    setSubsParam('size', String(size))
  }

  const onSubsSearch = (term: string | null): void => {
    subsGateRef.current.drop()
    setSubsTokens(resetTokens())
    setSubsLoading(true)
    setSubsSearch(term)
  }

  const handleReconnect = async (): Promise<void> => {
    if (auth0Token == null || bot == null || reconnecting) return
    setReconnecting(true)
    setReconnectError(null)
    try {
      const { result } = await graphqlRequest<{ result: ReconnectBotResult }>(
        reconnectBotMutation,
        { botUserId: bot.userId },
        auth0Token
      )
      const failed = result.channels.filter((c) => c.status !== 'recreated')
      if (failed.length > 0) {
        setReconnectError(
          `Recreated ${result.channels.length - failed.length} of ${result.channels.length} channels. ` +
          `Still failing: ${failed.map((c) => `#${c.channelLogin} (${c.error ?? 'unknown'})`).join(', ')}`
        )
      }
      void fetchBot()
      reloadSubscriptions()
    } catch (err) {
      setReconnectError(err instanceof Error ? err.message : 'Failed to reconnect channels')
    } finally {
      setReconnecting(false)
    }
  }

  const handleForceUnsubscribe = async (sub: AdminSubscriptionRow): Promise<void> => {
    if (auth0Token === undefined || unsubscribingId !== null) return
    const ok = globalThis.confirm(
      `Force-unsubscribe ${sub.channelLogin}? The bot will stop receiving chat from this channel. ` +
      'This is intended for stuck/orphaned subscriptions; if any stream is still bound to it, ' +
      'remove it from there instead.'
    )
    if (!ok) return
    setUnsubscribingId(sub.subscriptionId)
    setForceError(null)
    try {
      await graphqlRequest<{ result: UnsubscribeResult }>(
        unsubscribeChannelMutation,
        { subscriptionId: sub.subscriptionId },
        auth0Token
      )
      reloadSubscriptions()
    } catch (err) {
      setForceError(err instanceof Error ? err.message : 'Failed to unsubscribe')
    } finally {
      setUnsubscribingId(null)
    }
  }

  const handleConnect = async (): Promise<void> => {
    if (auth0Token === undefined || connecting) return
    setConnecting(true)
    try {
      const { authUrl } = await graphqlRequest<{ authUrl: TwitchAuthUrl }>(
        requestTwitchAuthUrlMutation,
        {},
        auth0Token
      )

      const popup = globalThis.open(
        authUrl.url,
        'twitch-bot-auth',
        'width=520,height=720,menubar=no,toolbar=no'
      )

      if (popup === null) {
        globalThis.open(authUrl.url, '_blank', 'noopener,noreferrer')
        setConnecting(false)
        return
      }

      if (popupWatcherRef.current !== null) {
        globalThis.clearInterval(popupWatcherRef.current)
      }
      popupWatcherRef.current = globalThis.setInterval(() => {
        if (popup.closed) {
          globalThis.clearInterval(popupWatcherRef.current ?? undefined)
          popupWatcherRef.current = null
          setConnecting(false)
          void fetchBot()
          reloadSubscriptions()
        }
      }, 800)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Twitch auth')
      setConnecting(false)
    }
  }

  if (streamToolsUser === null) return <div>Unauthorized</div>
  if (!superuser) return null
  if (auth0Token === undefined) return <LoadingPage />

  return (
    <div className='flex flex-1' data-testid='bots-page'>
      <main className='flex flex-1 flex-col overflow-y-auto px-4 py-4'>
        <header className='mb-6 flex items-center gap-3'>
          <h1 className='text-2xl font-semibold text-dm-text-primary'>Admin</h1>
        </header>
        <AdminTotalsSection auth0Token={auth0Token} />
        <section data-testid='bots-section' className='flex flex-1 flex-col'>
          <div className='mb-4 flex items-center justify-between gap-3'>
            <img src='/images/twitch_wordmark_flat_purple.png' alt='Twitch' className='h-5 pl-2 w-auto' />
            {/* <div className='flex items-baseline gap-3' /> */}
            <button
              onClick={() => {
                void fetchBot()
                reloadSubscriptions()
              }}
              disabled={botLoading || subsLoading}
              title='Refresh bot and channel-subscription data'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              <ArrowPathIcon className={`size-3.5 ${botLoading || subsLoading ? 'animate-spin' : ''}`} aria-hidden='true' />
              Refresh
            </button>
          </div>

          {error !== null && (
            <div className='mb-6'>
              <ErrorBanner message={error} onRetry={() => { void fetchBot() }} />
            </div>
          )}

          {botLoading && !botLoaded
            ? (
              <div className='flex flex-1 items-center justify-center py-16'>
                <LoadingSpinner />
              </div>
              )
            : bot == null
              ? (
                <div className='flex flex-1 items-center justify-center py-12'>
                  <ConnectEmptyState onConnect={() => { void handleConnect() }} connecting={connecting} />
                </div>
                )
              : (
                <>
                  <BotDetailCard
                    bot={bot}
                    connecting={connecting}
                    revokedCount={subsPage?.counts.revoked ?? 0}
                    reconnecting={reconnecting}
                    reconnectError={reconnectError}
                    onReauth={() => { void handleConnect() }}
                    onReconnect={() => { void handleReconnect() }}
                    onDismissReconnectError={() => setReconnectError(null)}
                  />
                  <SubscriptionsCard
                    page={subsPage}
                    loading={subsLoading}
                    error={subsError}
                    onRetry={reloadSubscriptions}
                    view={subsView}
                    onView={onSubsView}
                    pageSize={subsPageSize}
                    onPageSize={onSubsPageSize}
                    urlNotice={subsUrlNotice}
                    activeSearch={subsSearch}
                    onSearch={onSubsSearch}
                    pageNumber={subsSearch != null ? 1 : pageNumberOf(subsTokens)}
                    onPrev={() => {
                      if (!subsGateRef.current.takePrev()) return
                      setSubsLoading(true)
                      setSubsTokens((prev) => popToken(prev))
                    }}
                    onNext={() => {
                      const token = subsGateRef.current.takeNext()
                      if (token == null) return
                      setSubsLoading(true)
                      setSubsTokens((prev) => pushToken(prev, token))
                    }}
                    unsubscribingId={unsubscribingId}
                    onForceUnsubscribe={(sub) => { void handleForceUnsubscribe(sub) }}
                    forceError={forceError}
                  />
                </>
                )}

          {bot != null && (
            <p className='mt-6 flex items-center gap-1.5 text-xs text-dm-text-accent'>
              <ArrowTopRightOnSquareIcon className='size-3.5' aria-hidden='true' />
              Authorizing again will replace the current bot record on the backend.
            </p>
          )}
        </section>
        <section data-testid='youtube-admin-section' className='mt-10'>
          <h2 className='flex items-center'>
            <img src='/images/yt_logo_fullcolor_white_digital.png' alt='YouTube' className='h-5 pl-2 w-auto' />
          </h2>
          <YouTubeInviteCard auth0Token={auth0Token} />
          <YouTubeDemoExemptCard auth0Token={auth0Token} />
          <YouTubeWaitlistCard auth0Token={auth0Token} />
          <YouTubeTelemetryCard auth0Token={auth0Token} />
        </section>
        <section data-testid='account-admin-section' className='mt-10'>
          <h2 className='flex items-center gap-2 text-lg font-semibold text-dm-text-primary'>
            <ShieldExclamationIcon className='size-5 text-brand' aria-hidden='true' />
            Accounts
          </h2>
          <SuspendAccountCard auth0Token={auth0Token} />
        </section>
        <MerchAdminSection auth0Token={auth0Token} />
      </main>
    </div>
  )
}
